import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Brain, Save, PlusCircle, Tag, RefreshCw, Code, Users, Search, MapPin, Building2, Wrench, Globe, GraduationCap, Book, Briefcase } from 'lucide-react';
import { peerSupportService } from '../services/api';
import { Alert, AlertDescription } from '../components/ui/alert';

const REGIONS_AND_STATES = {
  "Northeast": [
    "Connecticut", "Maine", "Massachusetts", "New Hampshire", 
    "New Jersey", "New York", "Pennsylvania", "Rhode Island", "Vermont"
  ],
  "Southeast": [
    "Alabama", "Arkansas", "Delaware", "Florida", "Georgia", 
    "Kentucky", "Louisiana", "Maryland", "Mississippi", 
    "North Carolina", "South Carolina", "Tennessee", 
    "Virginia", "West Virginia"
  ],
  "Midwest": [
    "Illinois", "Indiana", "Iowa", "Kansas", "Michigan", 
    "Minnesota", "Missouri", "Nebraska", "North Dakota", 
    "Ohio", "South Dakota", "Wisconsin"
  ],
  "Southwest": [
    "Arizona", "New Mexico", "Oklahoma", "Texas"
  ],
  "West": [
    "California", "Colorado", "Idaho", "Montana", "Nevada", 
    "Oregon", "Utah", "Washington", "Wyoming"
  ],
  "Other": [
    "Alaska", "Hawaii"
  ]
};

const MyPeerSupportGroups = () => {
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });

  // Get all states for dropdown
  const allStates = Object.values(REGIONS_AND_STATES).flat().sort();

  // Fetch groups data
  // Fetch user's selected groups
  const fetchUserGroups = async () => {
    try {
      const userGroups = await peerSupportService.getUserGroups();
      setSelectedGroups(userGroups);
    } catch (error) {
      console.error('Error loading user groups:', error);
      setError('Failed to load your selected groups. Please try again later.');
    }
  };

  useEffect(() => {
    fetchUserGroups();
  }, []);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const baseUrl = process.env.PUBLIC_URL || window.location.origin;
        const response = await fetch(`${baseUrl}/peersupportgroups.json`);
        
        if (!response.ok) {
          throw new Error('Failed to load peer support groups data');
        }

        const data = await response.json();
        setGroups(data.peerSupportGroups);
        setFilteredGroups(data.peerSupportGroups);
      } catch (error) {
        console.error('Error loading peer support groups:', error);
        setError('Failed to load peer support groups. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGroups();
  }, []);

  // Filter groups based on search criteria
  useEffect(() => {
    const filterGroups = () => {
      let filtered = [...groups];

      // Filter by state if selected
      if (selectedState) {
        filtered = filtered.filter(group => 
          group.state.toLowerCase() === selectedState.toLowerCase()
        );
      }

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(group =>
          group.name.toLowerCase().includes(query) ||
          group.type.toLowerCase().includes(query)
        );
      }

      setFilteredGroups(filtered);
    };

    filterGroups();
  }, [groups, selectedState, searchQuery]);

  const handleClearAllGroups = async () => {
    try {
      setSaveStatus({ type: '', message: '' });
      await peerSupportService.saveGroups([]);
      setSelectedGroups([]);
      setSaveStatus({
        type: 'success',
        message: 'All groups have been removed.'
      });
    } catch (error) {
      console.error('Error clearing groups:', error);
      setSaveStatus({
        type: 'error',
        message: 'Failed to clear groups. Please try again.'
      });
    }
  };

  const handleGroupToggle = async (group) => {
    try {
      setSaveStatus({ type: '', message: '' });
      const isSelected = selectedGroups.some(g => g.name === group.name);
      
      let newSelectedGroups;
      if (isSelected) {
        newSelectedGroups = selectedGroups.filter(g => g.name !== group.name);
      } else {
        newSelectedGroups = [...selectedGroups, group];
      }
      
      await peerSupportService.saveGroups(newSelectedGroups);
      setSelectedGroups(newSelectedGroups);
      
      setSaveStatus({
        type: 'success',
        message: isSelected ? 'Group removed successfully.' : 'Group added successfully.'
      });
    } catch (error) {
      console.error('Error toggling group:', error);
      setSaveStatus({
        type: 'error',
        message: 'Failed to update group selection. Please try again.'
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Card className="w-full mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-6 w-6" />
            Peer Support Groups Directory
          </CardTitle>
          <div className="mt-4 text-gray-600">
            <p className="mb-4">
              Connect with peer support groups in your area that understand your unique experiences
              and challenges. These groups offer valuable resources, emotional support, and opportunities
              to share knowledge with others who are on similar journeys.
            </p>
          </div>
        </CardHeader>

        <CardContent>
          {(error || saveStatus.message) && (
            <Alert className={`mb-6 ${
              error || saveStatus.type === 'error' ? 'bg-red-50' : 'bg-green-50'
            }`}>
              <AlertDescription>
                {error || saveStatus.message}
              </AlertDescription>
            </Alert>
          )}
          
          {/* Selected Groups Summary */}
          {selectedGroups.length > 0 && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Your Selected Groups</h3>
                <button
                  onClick={() => handleClearAllGroups()}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2"
                  disabled={selectedGroups.length === 0}
                >
                  Clear All
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedGroups.map((group, index) => (
                  <Card key={index} className="bg-blue-50">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <h4 className="font-medium">{group.name}</h4>
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="h-4 w-4" />
                            <span>{group.state}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Tag className="h-4 w-4" />
                            <span>{group.type}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleGroupToggle(group)}
                          className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2"
                        >
                          Remove
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {error && (
            <Alert className="bg-red-50 mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-6">
            {/* Search Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* State Selection */}
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-gray-700">State</label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full p-2 border rounded-md bg-white"
                >
                  <option value="">All States</option>
                  {allStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              {/* Search Input */}
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-gray-700">Search Groups</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by group name or type..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full p-2 border rounded-md pl-8"
                  />
                  <Search className="h-4 w-4 absolute left-2 top-3 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-4">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading support groups...</p>
                </div>
              ) : filteredGroups.length === 0 ? (
                <Alert className="bg-blue-50">
                  <AlertDescription>
                    No support groups found matching your criteria. Try adjusting your search.
                  </AlertDescription>
                </Alert>
              ) : (
                filteredGroups.map((group, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold">{group.name}</h3>
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="h-4 w-4" />
                            <span>{group.state}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Tag className="h-4 w-4" />
                            <span>{group.type}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => handleGroupToggle(group)}
                            className={`px-4 py-2 rounded-md flex items-center gap-2 ${
                              selectedGroups.some(g => g.name === group.name)
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-blue-600 hover:bg-blue-700'
                            } text-white`}
                          >
                            {selectedGroups.some(g => g.name === group.name) ? 'Remove' : 'Select'}
                          </button>
                          <button className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">
                            Contact
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyPeerSupportGroups;